import { lazy, Suspense } from "react"
import type { ReactNode } from "react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import { Toaster } from "sonner"
import AppLayout from "./components/layouts/AppLayout.tsx"
import { can, firstAllowedPath } from "./services/permissions.ts"
import NotFound from "./pages/NotFound.tsx"
import { RequireAuth } from "./components/RequireAuth.tsx"
import { SecurityDoorSettingsProvider } from "./context/security-door-settings-context.tsx"

// Κάθε σελίδα φορτώνεται on-demand. Πριν, και οι 21 ήταν στατικά imports, οπότε
// το recharts (~108 kB gzip, το χρησιμοποιεί ΜΟΝΟ το Dashboard) κατέβαινε σε
// κάθε route — ακόμα και σε σελίδες χωρίς κανένα γράφημα.
// Το AppLayout μένει eager: είναι το κέλυφος, χρειάζεται αμέσως.
const Dashboard = lazy(() => import("./pages/Dashboard.tsx"))
const InventoryLanding = lazy(() => import("./pages/inventory/InventoryLanding.tsx"))
const SecurityDoorsInventory = lazy(() => import("./pages/inventory/SecurityDoorsInventory.tsx"))
const InteriorDoorsInventory = lazy(() => import("./pages/inventory/InteriorDoorsInventory.tsx"))
const Shortages = lazy(() => import("./pages/inventory/Shortages.tsx"))
const AluminiumOrders = lazy(() => import("./pages/inventory/AluminiumOrders.tsx"))
const SecurityDoorsOrders = lazy(() => import("./pages/orders/SecurityDoorsOrders.tsx"))
const InteriorDoorsOrders = lazy(() => import("./pages/orders/InteriorDoorsOrders.tsx"))
const NewOrderWizard = lazy(() => import("./pages/orders/NewOrderWizard.tsx"))
const NewInteriorOrderWizard = lazy(() => import("./pages/orders/NewInteriorOrderWizard.tsx"))
const OrderDetail = lazy(() => import("./pages/orders/OrderDetail.tsx"))
const OrdersHistory = lazy(() => import("./pages/orders/OrdersHistory.tsx"))
const ProductionBoard = lazy(() => import("./pages/orders/ProductionBoard.tsx"))
const ProductionOrderView = lazy(() => import("./pages/orders/ProductionOrderView.tsx"))
const Customers = lazy(() => import("./pages/Customers.tsx"))
const CustomerDetail = lazy(() => import("./pages/customers/CustomerDetail.tsx"))
const Settings = lazy(() => import("./pages/settings/Settings.tsx"))
const SuppliersSettings = lazy(() => import("./pages/settings/SuppliersSettings.tsx"))
const SecurityDoorColorsSettings = lazy(() => import("./pages/settings/SecurityDoorColorsSettings.tsx"))
const InteriorColorsSettings = lazy(() => import("./pages/settings/InteriorColorsSettings.tsx"))
const DesignsSettings = lazy(() => import("./pages/settings/DesignsSettings.tsx"))
const AccessoriesSettings = lazy(() => import("./pages/settings/AccessoriesSettings.tsx"))
const FrameSizesSettings = lazy(() => import("./pages/settings/FrameSizesSettings.tsx"))
const StaffSettings = lazy(() => import("./pages/settings/StaffSettings.tsx"))
const SecurityDoorKasaSizesSettings = lazy(() => import("./pages/settings/SecurityDoorKasaSizesSettings.tsx"))
const ScanResult = lazy(() => import("./pages/ScanResult.tsx"))
const Scanner = lazy(() => import("./pages/Scanner.tsx"))
const Login = lazy(() => import("./pages/Login.tsx"))
const Help = lazy(() => import("./pages/Help.tsx"))

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 1000 * 30 } },
})

/**
 * Διαδρομή που απαιτεί δικαίωμα.
 *
 * Ανακατευθύνει αντί να δείχνει «δεν έχετε πρόσβαση»: ο χρήστης δεν ζήτησε να
 * πάει εκεί — ένας σελιδοδείκτης, ένα παλιό link ή το μενού πριν την αλλαγή
 * δικαιωμάτων τον έστειλε. ΔΕΝ είναι ασφάλεια· το φράγμα είναι ο server.
 */
function RequirePermission({ resource, children }: { resource: string; children: ReactNode }) {
  if (!can(resource)) return <Navigate to={firstAllowedPath()} replace />
  return <>{children}</>
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route
            path="/login"
            element={
              <Suspense fallback={null}>
                <Login />
              </Suspense>
            }
          />
          <Route
            element={
              // Ο provider ΜΕΣΑ στο RequireAuth, όχι γύρω από όλο το App: έτσι μοντάρει
              // ΜΕΤΑ την είσοδο, με token ήδη στο localStorage — τα δύο getAll() του
              // πάνε αυθεντικοποιημένα (αλλιώς έπεφταν σε 401 στην οθόνη login και τα
              // dropdown χρωμάτων/μεγεθών έμεναν άδεια μέχρι F5).
              <RequireAuth>
                <SecurityDoorSettingsProvider>
                  <AppLayout />
                </SecurityDoorSettingsProvider>
              </RequireAuth>
            }
          >
            <Route path="/" element={<RequirePermission resource="dashboard"><Dashboard /></RequirePermission>} />
            {/* Χωρίς RequirePermission — ο οδηγός χρήσης αφορά κάθε συνδεδεμένο
                χρήστη, ό,τι ρόλο κι αν έχει, όχι ένα συγκεκριμένο resource. */}
            <Route path="/help" element={<Help />} />
            <Route path="/inventory" element={<RequirePermission resource="inventory"><InventoryLanding /></RequirePermission>} />
            <Route path="/inventory/security-doors" element={<RequirePermission resource="inventory"><SecurityDoorsInventory /></RequirePermission>} />
            <Route path="/inventory/interior-doors" element={<RequirePermission resource="inventory"><InteriorDoorsInventory /></RequirePermission>} />
            <Route path="/inventory/shortages" element={<RequirePermission resource="inventory"><Shortages /></RequirePermission>} />
            <Route path="/inventory/aluminium-orders" element={<RequirePermission resource="inventory"><AluminiumOrders /></RequirePermission>} />
            <Route path="/orders/security-doors" element={<RequirePermission resource="orders"><SecurityDoorsOrders /></RequirePermission>} />
            <Route path="/orders/interior-doors" element={<RequirePermission resource="orders"><InteriorDoorsOrders /></RequirePermission>} />
            <Route path="/orders/security-doors/new" element={<RequirePermission resource="orders"><NewOrderWizard /></RequirePermission>} />
            <Route path="/orders/interior-doors/new" element={<RequirePermission resource="orders"><NewInteriorOrderWizard /></RequirePermission>} />
            <Route path="/production" element={<RequirePermission resource="production"><ProductionBoard /></RequirePermission>} />
            {/* Η όψη παραγωγής μίας παραγγελίας — δικαίωμα `production`, ΟΧΙ
                `orders`: ο εργάτης που βλέπει τον πίνακα έχει πάντα αυτό. */}
            <Route path="/production/:id" element={<RequirePermission resource="production"><ProductionOrderView /></RequirePermission>} />
            <Route path="/orders/history" element={<RequirePermission resource="history"><OrdersHistory /></RequirePermission>} />
            {/* Τα δύο παλιά, ανά είδος, URL. Υπάρχουν σε σελιδοδείκτες και δεν
                πρέπει να σκάνε — ένα ιστορικό πλέον, με φίλτρο τύπου μέσα. */}
            <Route path="/orders/history/security-doors" element={<Navigate to="/orders/history" replace />} />
            <Route path="/orders/history/interior-doors" element={<Navigate to="/orders/history" replace />} />
            {/* Το σταθερό /scan πριν το παραμετρικό /scan/:id. */}
            <Route path="/scan" element={<RequirePermission resource="scan"><Scanner /></RequirePermission>} />
            <Route path="/scan/:id" element={<RequirePermission resource="scan"><ScanResult /></RequirePermission>} />
            {/* Τελευταίο: αλλιώς το :id θα έπιανε και το "history". */}
            <Route path="/orders/:id" element={<RequirePermission resource="orders"><OrderDetail /></RequirePermission>} />
            <Route path="/customers" element={<RequirePermission resource="customers"><Customers /></RequirePermission>} />
            <Route path="/customers/:id" element={<RequirePermission resource="customers"><CustomerDetail /></RequirePermission>} />
            <Route path="/settings" element={<RequirePermission resource="settings"><Settings /></RequirePermission>}>
              <Route index element={<Navigate to="suppliers" replace />} />
              <Route path="suppliers" element={<SuppliersSettings />} />
              <Route path="security-door-colors" element={<SecurityDoorColorsSettings />} />
              <Route path="interior-colors" element={<InteriorColorsSettings />} />
              <Route path="designs" element={<DesignsSettings />} />
              <Route path="accessories" element={<AccessoriesSettings />} />
              <Route path="frame-sizes" element={<FrameSizesSettings />} />
              <Route path="security-door-kasa-sizes" element={<SecurityDoorKasaSizesSettings />} />
              <Route path="staff" element={<RequirePermission resource="staff"><StaffSettings /></RequirePermission>} />
            </Route>
          </Route>
          {/* Χρήστης χωρίς κανένα δικαίωμα: σαφές μήνυμα αντί για λευκή οθόνη
              ή ατέρμονη ανακατεύθυνση. */}
          <Route
            path="/no-access"
            element={
              <div className="flex flex-col items-center gap-2 py-20 text-center">
                <p className="text-sm font-medium">Δεν έχετε πρόσβαση σε καμία ενότητα</p>
                <p className="text-xs text-muted-foreground">
                  Ζητήστε από τον διαχειριστή να σας δώσει δικαιώματα.
                </p>
              </div>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
